import { Button, HStack, Separator, VStack } from '@chakra-ui/react';
import { DraggablePanel } from '@components/ui/draggable';
import { Form } from '@components/ui/Form';
import { createToast, ToastStatusEnum } from '@components/ui/toaster';
import { useAppSelector } from '@hooks/redux.hooks';
import { useSelectorWithMapId } from '@hooks/useSelectorWithMapId';
import useValidation from '@hooks/useValidation';
import { selectActiveSite } from '@redux/authent/authent.selectors';
import { PlatformSelectors } from '@redux/config/config.selectors';
import { selectMapDrawPolygonsPoints } from '@redux/maps/maps.selectors';
import { selectCoordinatesUnit } from '@redux/settings/settings.selectors';
import {
  useCreateInitializationMaskingZoneMutation,
  useUpdateInitializationMaskingZoneMutation,
} from '@services/config/initializationMaskingZone.api';
import { useCreateNoFlyZoneMutation, useUpdateNoFlyZoneMutation } from '@services/config/noFlyZone.api';
import { useCreateNoInitZonesMutation, useUpdateNoInitZonesMutation } from '@services/config/noInitZone.api';
import { isNoFlyZoneType } from '@utils/map/zone.utils';
import { zoneOldSchema } from '@validations/zone.old.schema';
import { Formik, FormikErrors } from 'formik';
import { SetStateAction, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { v4 as uuidv4 } from 'uuid';

import { CoordinatesUnitEnum, Mark } from '@/types/commons/commons.types';
import {
  InitializationMaskingZone,
  NoFlyZone,
  NoInitZone,
  Zone2DTypeEnum,
  ZoneFormTypeEnum,
  ZoneTypeEnum,
} from '@/types/config/config.types';
import { ProcedureModel } from '@/types/config/procedure.types';
import { DroneAlertLevelEnum } from '@/types/data/data.types';
import { ZoneFormDisplayedFormatEnum } from '@/types/map.types';
import { PopupComponentProps, PopupTypeEnum } from '@/types/popup.types';

import ZoneApiError from './ZoneApiError';
import ZoneCoordinatesForm from './ZoneCoordinatesForm';
import ZoneDetailsForm from './ZoneDetailsForm';

export interface OldZoneForm {
  zoneName: string;
  zoneType: ZoneFormTypeEnum | undefined;
  marks: Mark[] | undefined;
  procedureModel: ProcedureModel | null;
  isZoneActive: boolean;
  displayedFormat: ZoneFormDisplayedFormatEnum;
  upperValue: number | null;
  lowerValue: number | null;
  isLowerValueEqualToGroundLevel: boolean;
  marginSize: number;
}

export default function ZoneFormPopup({
  mapConstraintsRef,
  top,
  left,
  data,
  onClose,
}: Readonly<PopupComponentProps<PopupTypeEnum.ZONE>>) {
  const { points, editedZone, zoneType } = data;
  const { formatMessage } = useIntl();
  const validationSchema = useValidation(zoneOldSchema);
  const userSettingUnit = useAppSelector(selectCoordinatesUnit);
  const activeSite = useAppSelector(selectActiveSite);
  const platforms = useAppSelector(PlatformSelectors.selectAllPlatforms);
  const siteAltitude = platforms[0]?.altitude ?? 0;
  const [unit, setUnit] = useState<CoordinatesUnitEnum>(userSettingUnit);
  const drawnPoints = useSelectorWithMapId(selectMapDrawPolygonsPoints);
  const [createNIZ, { isLoading: isLoadingCreateNIZ, isError: isErrorCreateNIZ, reset: resetCreateNIZ }] =
    useCreateNoInitZonesMutation();
  const [createNFZ, { isLoading: isLoadingCreateNFZ, isError: isErrorCreateNFZ, reset: resetCreateNFZ }] =
    useCreateNoFlyZoneMutation();
  const [createIMZ, { isLoading: isLoadingCreateIMZ, isError: isErrorCreateIMZ, reset: resetCreateIMZ }] =
    useCreateInitializationMaskingZoneMutation();
  const isCreateFormLoading = isLoadingCreateNIZ || isLoadingCreateNFZ || isLoadingCreateIMZ;
  const isCreateApiError = isErrorCreateNIZ || isErrorCreateNFZ || isErrorCreateIMZ;

  const isEditing = !!editedZone;

  const [updateNIZ, { isLoading: isLoadingUpdateNIZ, isError: isErrorUpdateNIZ, reset: resetUpdateNIZ }] =
    useUpdateNoInitZonesMutation();
  const [updateNFZ, { isLoading: isLoadingUpdateNFZ, isError: isErrorUpdateNFZ, reset: resetUpdateNFZ }] =
    useUpdateNoFlyZoneMutation();
  const [updateIMZ, { isLoading: isLoadingUpdateIMZ, isError: isErrorUpdateIMZ, reset: resetUpdateIMZ }] =
    useUpdateInitializationMaskingZoneMutation();
  const isUpdateFormLoading = isLoadingUpdateNIZ || isLoadingUpdateNFZ || isLoadingUpdateIMZ;
  const isUpdateApiError = isErrorUpdateNIZ || isErrorUpdateNFZ || isErrorUpdateIMZ;

  const initialZone: OldZoneForm = {
    zoneName: isEditing ? editedZone.name : '',
    zoneType: zoneType,
    marks: isEditing && zoneType && isNoFlyZoneType(zoneType) ? (editedZone as NoFlyZone).marks : [],
    procedureModel:
      isEditing && zoneType && isNoFlyZoneType(zoneType) ? (editedZone as NoFlyZone).procedureModel : null,
    displayedFormat:
      isEditing && (editedZone.heightMin || editedZone.heightMax)
        ? ZoneFormDisplayedFormatEnum.HEIGHT
        : ZoneFormDisplayedFormatEnum.ALTITUDE,
    marginSize: isEditing && 'marginSize' in editedZone ? editedZone.marginSize : 50,
    isZoneActive: isEditing ? editedZone.active : true,
    upperValue: isEditing ? (editedZone.altitudeMax ?? editedZone.heightMax ?? null) : 100,
    lowerValue: isEditing ? (editedZone.altitudeMin ?? editedZone.heightMin ?? null) : 0,
    isLowerValueEqualToGroundLevel: isEditing ? editedZone.heightMin === 0 : true,
  };

  function handleHeightToAltitudeChange(
    values: OldZoneForm,
    setValues: (
      values: SetStateAction<OldZoneForm>,
      shouldValidate?: boolean,
    ) => Promise<void | FormikErrors<OldZoneForm>>,
  ) {
    let newValues;

    if (values.displayedFormat === ZoneFormDisplayedFormatEnum.HEIGHT) {
      newValues = {
        ...values,
        upperValue: (values.upperValue ?? 0) + siteAltitude,
        lowerValue: (values.lowerValue ?? 0) + siteAltitude,
        displayedFormat: ZoneFormDisplayedFormatEnum.ALTITUDE,
      };
    } else {
      newValues = {
        ...values,
        upperValue: (values.upperValue ?? 0) - siteAltitude,
        lowerValue: (values.lowerValue ?? 0) - siteAltitude,
        displayedFormat: ZoneFormDisplayedFormatEnum.HEIGHT,
      };
    }
    setValues(newValues);
  }

  function zoneFormTypeToAlertLevelEnum(input: Zone2DTypeEnum): DroneAlertLevelEnum | null {
    switch (input) {
      case Zone2DTypeEnum.NFZ_INFO:
        return DroneAlertLevelEnum.DRONE_INFO;

      case Zone2DTypeEnum.NFZ_WARNING:
        return DroneAlertLevelEnum.DRONE_WARNING;

      case Zone2DTypeEnum.NFZ_CRITICAL:
        return DroneAlertLevelEnum.DRONE_CRITICAL;

      default:
        return null;
    }
  }

  function handleSuccess() {
    createToast(
      formatMessage({ id: editedZone ? 'zone.successUpdate' : 'zone.successCreate' }),
      ToastStatusEnum.SUCCESS,
    );
    onClose();
  }

  function submitZone({
    zoneName,
    zoneType,
    marks,
    procedureModel,
    isZoneActive,
    displayedFormat,
    marginSize,
    upperValue,
    lowerValue,
  }: OldZoneForm) {
    const dynamicZoneData = {
      name: zoneName,
      polygon: drawnPoints ?? points,
      active: isZoneActive,
      heightMin: displayedFormat === ZoneFormDisplayedFormatEnum.HEIGHT ? lowerValue : null,
      heightMax: displayedFormat === ZoneFormDisplayedFormatEnum.HEIGHT ? upperValue : null,
      altitudeMin: displayedFormat === ZoneFormDisplayedFormatEnum.ALTITUDE ? lowerValue : null,
      altitudeMax: displayedFormat === ZoneFormDisplayedFormatEnum.ALTITUDE ? upperValue : null,
    };

    const zoneCreateToSend = {
      ...dynamicZoneData,
      code: uuidv4(),
      site: activeSite !== null ? activeSite.code : 'local',
      minAltitudeFinite: true,
      maxAltitudeFinite: true,
    };

    if (editedZone) {
      switch (zoneType) {
        case Zone2DTypeEnum.NIZ:
          updateNIZ({
            id: editedZone.id,
            niz: {
              ...(editedZone as NoInitZone),
              ...dynamicZoneData,
            },
          })
            .unwrap()
            .then(handleSuccess);
          break;
        case Zone2DTypeEnum.NFZ_INFO:
        case Zone2DTypeEnum.NFZ_WARNING:
        case Zone2DTypeEnum.NFZ_CRITICAL:
          updateNFZ({
            id: editedZone.id,
            nfz: {
              ...(editedZone as NoFlyZone),
              ...dynamicZoneData,
              marks: marks ?? [],
              procedureModel: procedureModel!,
            },
          })
            .unwrap()
            .then(handleSuccess);
          break;
        case Zone2DTypeEnum.IMZ:
          if (marginSize >= 0) {
            updateIMZ({
              id: editedZone.id,
              imz: {
                ...(editedZone as InitializationMaskingZone),
                ...dynamicZoneData,
                marginSize,
              },
            })
              .unwrap()
              .then(handleSuccess);
          }
          break;
        default:
          break;
      }
    } else {
      switch (zoneType) {
        case Zone2DTypeEnum.NIZ:
          createNIZ({ ...zoneCreateToSend, type: ZoneTypeEnum.NIZ })
            .unwrap()
            .then(handleSuccess);
          break;
        case Zone2DTypeEnum.NFZ_INFO:
        case Zone2DTypeEnum.NFZ_WARNING:
        case Zone2DTypeEnum.NFZ_CRITICAL:
          createNFZ({
            ...zoneCreateToSend,
            type: ZoneTypeEnum.NFZ,
            level: zoneFormTypeToAlertLevelEnum(zoneType)!,
            marks: marks ?? [],
            procedureModel: procedureModel!,
          })
            .unwrap()
            .then(handleSuccess);
          break;
        case Zone2DTypeEnum.IMZ:
          if (marginSize >= 0) {
            createIMZ({
              ...zoneCreateToSend,
              type: ZoneTypeEnum.IMZ,
              marginSize,
            })
              .unwrap()
              .then(handleSuccess);
          }
          break;
        default:
          break;
      }
    }
  }

  return (
    <DraggablePanel.Root constraintsRef={mapConstraintsRef} top={top} left={left}>
      <DraggablePanel.Header onClose={onClose}>
        <FormattedMessage id="zone.creation" />
      </DraggablePanel.Header>
      <DraggablePanel.Body width={1061} height={737}>
        <ZoneApiError
          isVisible={isCreateApiError || isUpdateApiError}
          onClick={() => {
            if (isErrorCreateNIZ) {
              resetCreateNIZ();
            }
            if (isErrorCreateNFZ) {
              resetCreateNFZ();
            }
            if (isErrorCreateIMZ) {
              resetCreateIMZ();
            }
            if (isErrorUpdateNIZ) {
              resetUpdateNIZ();
            }
            if (isErrorUpdateNFZ) {
              resetUpdateNFZ();
            }
            if (isErrorUpdateIMZ) {
              resetUpdateIMZ();
            }
          }}
        />
        <Formik initialValues={initialZone} onSubmit={submitZone} validationSchema={validationSchema}>
          {({ handleSubmit, errors, touched, dirty, isValid, values, setFieldTouched, setValues, setFieldValue }) => (
            <Form onSubmit={handleSubmit} style={{ height: '100%' }}>
              <HStack height="100%" width="100%" gap={0}>
                <ZoneDetailsForm
                  touched={touched}
                  errors={errors}
                  values={values}
                  isZoneTypeDisabled={!!editedZone}
                  unit={unit}
                  setUnit={setUnit}
                  setFieldTouched={setFieldTouched}
                  setFieldValue={setFieldValue}
                  handleHeightToAltitudeChange={() => handleHeightToAltitudeChange(values, setValues)}
                />
                <VStack gap={0} width="100%" height="100%">
                  <ZoneCoordinatesForm
                    touched={touched}
                    errors={errors}
                    values={values}
                    unit={unit}
                    points={drawnPoints ?? points}
                    setFieldTouched={setFieldTouched}
                    setFieldValue={setFieldValue}
                  />
                  <Separator borderColor="neutral.600" />
                  <HStack
                    height="72px"
                    width="100%"
                    flexShrink={0}
                    justifyContent="end"
                    backgroundColor="neutral.800"
                    gap={2}
                    padding={2}
                  >
                    <Button type="button" onClick={onClose} width="120px">
                      <FormattedMessage id="global.cancel" />
                    </Button>
                    <Button
                      type="submit"
                      disabled={(!editedZone && !dirty) || !isValid || isCreateFormLoading || isUpdateFormLoading}
                      loading={isCreateFormLoading || isUpdateFormLoading}
                      width="180px"
                    >
                      <FormattedMessage id={editedZone ? 'zone.submitUpdate' : 'zone.submitCreate'} />
                    </Button>
                  </HStack>
                </VStack>
              </HStack>
            </Form>
          )}
        </Formik>
      </DraggablePanel.Body>
    </DraggablePanel.Root>
  );
}
